import type { Key } from "react-stately";

import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { useListData } from "react-stately";

import type { LinkableRequest } from "@/features/request/edit/hooks/useGetLinkableRequests";

import { Badge } from "@/components/badge";
import { Label } from "@/components/field";
import { Link } from "@/components/link";
import {
  MultiSelect,
  MultiSelectField,
  MultiSelectItem,
} from "@/components/multi-select";
import { Skeleton } from "@/components/skeleton";
import { Tag } from "@/components/tag-group";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useGetLinkableRequests } from "@/features/request/edit/hooks/useGetLinkableRequests";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";

const REQUEST_TYPE_LABELS: Record<string, string> = {
  graphic: "Graphic",
  video: "Video",
  website_design: "Website",
  website_implementation: "Website",
};

function getRequestTypeLabel(type: string) {
  return REQUEST_TYPE_LABELS[type];
}

export function LinkedRequestsSelect({
  className,
  isDisabled,
}: {
  className?: string;
  isDisabled?: boolean;
}) {
  const methods = useEditRequestFormContext();
  const { data, isFetching } = useGetLinkableRequests();
  const { updateField } = useFieldUpdate();
  const { isTemplate } = useIsTemplate();
  const defaultItems = methods.getValues("linkedRequestIds");

  const selectedList = useListData<LinkableRequest>({
    getKey: (item) => item.id,
    initialItems: data?.filter((item) => defaultItems?.includes(item.id)) ?? [],
  });

  useEffect(() => {
    if (data && defaultItems) {
      selectedList.remove(...selectedList.items.map((item) => item.id));
      selectedList.append(
        ...data.filter((item) => defaultItems.includes(Number(item.id))),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultItems]);

  if (isFetching) {
    return (
      <div className="flex flex-col gap-sm">
        <Label requiredHint>Linked requests</Label>
        <Skeleton className="h-[42px] w-full rounded-md" />
      </div>
    );
  }

  if (data) {
    return (
      <Controller
        control={methods.control}
        name="linkedRequestIds"
        render={({ field: { name, onBlur, onChange, value } }) => (
          <MultiSelectField className={className}>
            <Label>Linked requests</Label>
            <MultiSelect
              isDisabled={isDisabled}
              isInvalid={isFetching}
              items={data}
              name={name}
              onBlur={onBlur}
              onItemAdd={(key) => {
                const newValue = [...(value || []), key as number];

                onChange(newValue);
                void updateField(name, newValue);
              }}
              onItemRemove={(key) => {
                const newValue = (value || []).filter((id: Key) => id !== key);

                onChange(newValue);
                void updateField(name, newValue);
              }}
              placeholder="Search requests..."
              renderEmptyState={(inputValue) => (
                <span className="p-2 text-text-primary">
                  {inputValue ? (
                    <>No results found for: {inputValue}</>
                  ) : (
                    "No requests available"
                  )}
                </span>
              )}
              selectedList={selectedList}
              tag={(item) => (
                <Tag isDisabled={isDisabled}>
                  {isTemplate ? (
                    <span className="text-xs text-fg-brand-primary">
                      {`${item.id} - ${item.textValue}`}
                    </span>
                  ) : (
                    <Link
                      className="text-xs text-fg-brand-primary underline"
                      href={`/requests/${item.id}/edit`}
                      onClick={(e) => e.stopPropagation()}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {`${item.id} - ${item.textValue}`}
                    </Link>
                  )}
                </Tag>
              )}
            >
              {(item) => (
                <MultiSelectItem id={item.id} textValue={item.textValue}>
                  {item.textValue}
                  {item.type && (
                    <Badge isRounded size="medium" variant="purple">
                      {getRequestTypeLabel(item.type)}
                    </Badge>
                  )}
                </MultiSelectItem>
              )}
            </MultiSelect>
          </MultiSelectField>
        )}
      />
    );
  }
}
